import './SelectInput.scss';

import * as React from 'react';
import { makeTranslatable, TranslatableType } from '../interfaces/Translatable';
import { TestableInterface } from '../interfaces/TestableInterface';
import { ValidatableInterface } from '../interfaces/ValidatableInterface';
import { GlobalEvents } from '../../../Other/GlobalEvents';
import { RequiredInterface } from '../Required/RequiredInterface';
import { formatRequiredIndicator } from '../Required/Required';

const MAX_ITEMS = 10;

export interface SelectInputProps extends TestableInterface, ValidatableInterface, RequiredInterface {
  label?: TranslatableType;
  value: any | any[];
  map: { [key: string]: string };
  onChange?: (value) => void;
  onBlur?: (value: string) => void;
  multi?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  selectPlaceholder?: string;
  labelPosition?: 'left' | 'top';
  borderless?: boolean;
  addNewLabel?: string;
  onAddNew?: () => void;
  openDirection?: 'up' | 'down';
  orderBy?: 'key' | 'value';
  disabled?: boolean;
}

export interface SelectInputState {
  menuOpen: boolean;
  search: string;
}

export class SelectInput extends React.Component<SelectInputProps, SelectInputState> {
  private isInvoiceType = false;
  constructor(props: SelectInputProps) {
    super(props);

    this.state = {
      menuOpen: false,
      search: ''
    };
  }

  public static defaultProps: SelectInputProps = {
    value: null,
    map: {},
    multi: false,
    openDirection: null
  };

  // get text value for selected key
  private getSelectedValue() {
    // try-catch here because somehow testing 'if (this.props.value && this.props.map[this.props.value])'
    // returns false if value is '0'.
    try {
      if (Array.isArray(this.props.value)) {
        let result;
        this.props.value.forEach((value) => {
          const val = this.props.map[value];
          if (!result) {
            result = val;
          } else {
            result += ', ' + val;
          }
        });
        return result;
      } else {
        return this.props.map[this.props.value];
      }
    } catch (e) {
      console.error('Value missing in map');
    }
  }

  // onChange event for the whole component
  private handleOnChange(e: Event) {
    const value: string = (e.target as HTMLDivElement).getAttribute('value');
    this.props.onChange(value);
  }

  // just close the list div
  private handleOnBlur() {
    this.setState({ menuOpen: false });
  }

  private e;

  public componentDidMount() {
    this.e = GlobalEvents.Click((e) => {
      this.handleOnBlur();
    });
  }

  public componentDidUpdate() {
    if (this.searchInput) {
      this.searchInput.focus();
    }
  }
  public componentWillUnmount() {
    GlobalEvents.unBind(this.e);
  }

  // select value from the list
  private handleSelect(e: Event) {
    const value: string = (e.target as HTMLDivElement).getAttribute('value');
    this.props.onChange(value);
  }

  private handleValueClick(e: Event) {
    const value: string = (e.target as HTMLInputElement).getAttribute('value');
    this.handleClickOnMultiSelect(value);
    GlobalEvents.prevent(this.e);
    return false;
  }

  private handleCheckBoxClick(e: Event) {
    const value: string = (e.target as HTMLInputElement).parentElement.getAttribute('value');
    return this.handleClickOnMultiSelect(value);
  }

  private handleClickOnMultiSelect(value) {
    const options = [];
    if (!this.props.value) {
      options.push(value);
    } else if (this.props.value.indexOf(value) >= 0) {
      // remove clicked value from array
      this.props.value.forEach((val) => {
        if (val !== value) {
          options.push(val);
        }
      });
    } else {
      // add clicked value to array in its place
      Object.keys(this.props.map).forEach((key) => {
        if (this.props.value.indexOf(key) >= 0 || key === value) {
          options.push(key);
        }
      });
    }
    this.props.onChange(options);
  }

  private handleKeyPress(e: KeyboardEvent) {
    if (e.keyCode === 27) {
      if (this.state.search !== '') {
        this.setState({ search: '' });
      } else {
        this.setState({ menuOpen: false });
      }
    } else if (e.keyCode === 13 && this.onlyOption) {
      this.props.onChange(this.onlyOption);
      this.setState({
        search: '',
        menuOpen: false
      });
    }
  }

  private onlyOption = null;
  private searchInput = null;

  private renderSelectMenu() {
    const result = [];
    const self = this;

    if (this.props.searchable && Object.keys(this.props.map).length > MAX_ITEMS) {
      result.push(
        <div
          className="search"
          key="search"
          onClick={() => {
            GlobalEvents.prevent(this.e);
            return false;
          }}
        >
          <input
            type="text"
            ref={(input) => {
              this.searchInput = input;
            }}
            data-automation={this.props.automation + '-search'}
            value={this.state.search}
            onKeyUp={this.handleKeyPress.bind(this)}
            onChange={(e) => {
              this.setState({ search: e.currentTarget.value });
            }}
            placeholder={this.props.searchPlaceholder}
          />
          <span
            className="clear"
            data-automation={this.props.automation + '-clear'}
            onClick={() => {
              this.setState({ search: '' });
            }}
          >
            x
          </span>
        </div>
      );
    }

    let options = [];
    Object.keys(this.props.map).forEach((key) => {
      const value = self.props.map[key];
      if (
        self.state.search === '' ||
        value
          .toString()
          .toLowerCase()
          .indexOf(self.state.search.toLowerCase()) >= 0
      ) {
        options.push({ key, value });
      }
    });
    // order if necessary
    if (this.props.orderBy) {
      options = options.sort((a, b) => {
        if (a[this.props.orderBy] < b[this.props.orderBy]) {
          return -1;
        }
        if (a[this.props.orderBy] > b[this.props.orderBy]) {
          return 1;
        }
        return 0;
      });
    }

    if (options.length === 1) {
      this.onlyOption = options[0].key;
    } else {
      this.onlyOption = null;
    }

    // CORE-3285 make the vat exemption the third item
    // We have to do this because the types are sorted by id
    const vatlessId = '127';
    if (this.isInvoiceType && options.find((o) => o.id === vatlessId)) {
      options = options.filter((o) => o.id !== vatlessId);
      options.splice(2, 0, vatlessId);
    }
    options.forEach((o) => {
      if (self.props.multi) {
        let checked = false;
        if (self.props.value && self.props.value.indexOf(o.key) > -1) {
          checked = true;
        }
        result.push(
          // @ts-ignore
          <div className="option" key={o.key} value={o.key} onClick={self.handleValueClick.bind(self)}>
            <input type="checkbox" checked={checked} onChange={self.handleCheckBoxClick.bind(self)} />
            {o.value}
          </div>
        );
      } else {
        result.push(
          // @ts-ignore
          <div
            key={o.key}
            value={o.key}
            className={
              'option' + (o.key === self.props.value || (self.onlyOption && o.key === self.onlyOption) ? ' active' : '')
            }
            onClick={self.handleSelect.bind(self)}
          >
            {o.value}
          </div>
        );
      }
    });

    return result;
  }

  private renderActionMenu() {
    const { addNewLabel, onAddNew } = this.props;

    if ((addNewLabel && !onAddNew) || (!addNewLabel && onAddNew)) {
      throw new Error(
        'Invalid prop "addNewLabel|onAddNew" supplied to SelectInput. Validation failed, please provide both'
      );
    }

    if (addNewLabel && onAddNew) {
      return (
        <div className="action-btn" onClick={onAddNew}>
          {this.props.addNewLabel}
        </div>
      );
    }

    return null;
  }

  public render(): JSX.Element {
    const classes = ['SelectInput', 'Controlled', 'Input'];
    const required = formatRequiredIndicator(this.props);
    if (this.props.labelPosition && this.props.labelPosition === 'top') {
      classes.push('LabelOnTop');
    }
    if (this.props.borderless) {
      classes.push('Borderless');
    }
    if (this.props.invalid) {
      classes.push('invalid');
    }
    if (this.props.disabled) {
      classes.push('SelectInput--disabled');
    }
    let label = null;
    this.isInvoiceType = this.props.automation ? this.props.automation.includes('invoice-type') : false;
    if (this.props.label) {
      label = (
        <span data-automation={this.props.automation + '-label'} className="Label">
          {makeTranslatable(this.props.label)}
          {required}
        </span>
      );
    }

    let menu = null;
    if (this.state.menuOpen) {
      const direction = this.props.openDirection || '';
      menu = (
        <div className={`select-menu ${direction}`} id={'menuDiv'}>
          {this.renderSelectMenu()}
          {this.renderActionMenu()}
        </div>
      );
      classes.push('menuOpen');
    }
    // Hackety hack to open menu upwards if it would go over the bottom edge of content div.
    // This is mostly for modals that may be smaller and/or scrollable.
    // Requires a parent div at some level with className 'content'
    const menuDiv = document.getElementById('menuDiv');
    if (menuDiv && !this.props.openDirection) {
      let parentDiv = menuDiv.parentElement;
      while (parentDiv.parentElement) {
        parentDiv = parentDiv.parentElement;
        if (parentDiv.className.indexOf('content') >= 0) {
          if (menuDiv.getBoundingClientRect().bottom > parentDiv.getBoundingClientRect().bottom) {
            menuDiv.className = 'select-menu up';
          }
          break;
        }
      }
    }

    return (
      // @ts-ignore
      <div
        className={classes.join(' ')}
        data-automation={this.props.automation}
        value={this.props.value}
        data-value={this.props.value}
      >
        {label}
        <div className="select Controlled">
          <div
            className="selected-value"
            onClick={(e) => {
              if (this.props.disabled) {
                return false;
              }
              this.setState({ menuOpen: !this.state.menuOpen });
              GlobalEvents.prevent(this.e);
              return false;
            }}
          >
            <span className="selected-value">
              {this.props.value !== null && this.getSelectedValue()}
              {!this.props.value && this.props.selectPlaceholder && (
                <span className="placeholder">{this.props.selectPlaceholder}</span>
              )}
            </span>
            <span className="arrow" />
          </div>
          {menu}
        </div>
      </div>
    );
  }
}
